import { cloneDeep, uniqBy } from 'lodash';
import {
  applySnapshot,
  cast,
  flow,
  getRoot,
  getSnapshot,
  types,
} from 'mobx-state-tree';
import moment from 'moment';
import { HouseModel, MemberBaseModel } from 'store/models/areasStore';
import {
  AddNewThirdPartyModal,
  RequestStateModel,
} from 'store/models/general-models';
import { AreaModel } from 'store/models/metersStore';
import { WorkerModel } from 'store/models/workersStore';
import { killNulls, safelyInvoke, splitPhone } from 'utils';
import {
  ApplicantSearchContext,
  ApplicantSearchGuard,
  IArea,
  IHouse,
  IMember,
  ITenant,
  IWorkerNew,
} from 'types';
import { AppealFile, IAppealsStore } from '../../../types';
import { ApplicantTypesList, CreateTabsList } from '../../../constants';
import { Areas, Tenants, Workers } from 'http-requests';
import { IOtherMemberKeys } from 'components/create-request/tenants/components/other-tenant/types';
import { OtherMemberModel } from '../../others';

const PROBLEM_LINK_PREFIX = 'https://gorod.gov.spb.ru/staff/cabinet/problems/';

const ContactTypeMST = types.enumeration('ContactTypesList', [
  'tenant',
  'worker',
  'other',
]);

const ThirdPartyTypeMST = types.enumeration('OtherType', ['physical', 'legal']);

const RequestsModel = types.model({
  socials: types.optional(RequestStateModel, {}),
  areas: types.optional(RequestStateModel, {}),
  addOtherMember: types.optional(RequestStateModel, {}),
  addThirdPartyAccount: types.optional(RequestStateModel, {}),
});

const ContactRequestsModel = types.model({
  members: types.optional(RequestStateModel, {}),
  workers: types.optional(RequestStateModel, {}),
  addContacts: types.optional(RequestStateModel, {}),
});

const ContactModel = types
  .model({
    loading: types.optional(ContactRequestsModel, {}),
    contactType: types.optional(ContactTypeMST, 'tenant'),
    thirdPartyType: types.optional(ThirdPartyTypeMST, 'physical'),
    members: types.optional(types.array(MemberBaseModel), []),
    workers: types.optional(types.array(WorkerModel), []),
    otherMember: types.optional(OtherMemberModel, {}),
    memberId: types.optional(types.string, ''),
    workerId: types.optional(types.string, ''),
    organizationId: types.optional(types.string, ''),
    legalId: types.optional(types.string, ''),
    physicalId: types.optional(types.string, ''),
  })
  .volatile(() => ({
    searchResultsGeneration: 0,
    searchResultsLoadingGeneration: 0,
  }))
  .views((self) => ({
    get isValidOtherTenant(): boolean {
      return (
        self.otherMember.last_name.length >= 2 &&
        self.otherMember.first_name.length >= 2 &&
        !!self.otherMember.phones &&
        self.otherMember.phones[0]?.code?.length === 3 &&
        self.otherMember.phones[0]?.number?.length === 7
      );
    },
  }))
  .actions((self) => ({
    setContactType(type: 'tenant' | 'worker' | 'other') {
      self.contactType = type;
    },
    setThirdPartyType(type: 'physical' | 'legal') {
      self.thirdPartyType = type;
    },
    setSelectedId(
      key:
        | 'memberId'
        | 'workerId'
        | 'organizationId'
        | 'legalId'
        | 'physicalId',
      id: string
    ) {
      self[key] = id;
    },
    // Аналог того, что делает getWorkersByPhone (self.workers = cast(...)),
    // но без сетевого поиска — нужен, когда сотрудник уже точно известен
    // (см. initFromHouseChatMessage) и искать его по телефону не нужно и
    // рискованно: getWorkersByPhone взял бы первого сотрудника с таким же
    // рабочим номером, но это не обязательно тот, кто создаёт обращение.
    setWorkers(workers: IWorkerNew[]) {
      self.workers = cast(workers);
    },
    changeOtherMemberParams(key: IOtherMemberKeys, value: any) {
      if (key === 'phones') {
        const phone = splitPhone(value);

        if (!!self.otherMember.phones.length) {
          self.otherMember.phones[0].code = phone.code;
          self.otherMember.phones[0].number = phone.number;
        } else {
          self.otherMember.phones.push({
            add: null,
            added_by_tenant: false,
            code: phone.code,
            number: '',
            id: null,
            not_actual: false,
            phone_type: 'cell',
            str_number: null,
          });
        }
      } else {
        self.otherMember[key] = value;
      }
    },
    editWorkersList(worker: IWorkerNew, phone: string) {
      const workerFind = !self.workers.find(
        (item: IWorkerNew) => item.id === worker.id
      );

      if (workerFind) {
        self.workers = self.workers.filter((work) =>
          work.phones.find((workerPhone) => workerPhone.str_number === phone)
        ) as any;
        self.workers.push(worker);
      }
    },
    clearMembers() {
      self.memberId = '';
      applySnapshot(self.loading.members, {});
      applySnapshot(self.members, []);
    },
    clearSearchResultsPreservingRequests() {
      self.searchResultsGeneration += 1;
      applySnapshot(self.members, []);
      applySnapshot(self.otherMember, {});
      applySnapshot(self.workers, []);
      self.memberId = '';
      self.workerId = '';
      self.organizationId = '';
      self.legalId = '';
      self.physicalId = '';

      Object.entries(self.loading).forEach(([key, value]) => {
        if (self.loading[key as keyof typeof self.loading].requestCount === 0) {
          value.clearStates();
        }
      });
    },
    invalidateSearchResultsForFullReset() {
      self.searchResultsGeneration += 1;
      self.searchResultsLoadingGeneration += 1;
    },
    getMembersByArea: flow(function* (
      areaId: string,
      other_tenants: boolean,
      shouldApply?: ApplicantSearchGuard,
      shouldFinalize?: ApplicantSearchGuard
    ) {
      const generation = self.searchResultsGeneration;
      const loadingGeneration = self.searchResultsLoadingGeneration;
      const canApply = () =>
        generation === self.searchResultsGeneration &&
        (shouldApply?.() ?? true);
      const canFinalize = () =>
        loadingGeneration === self.searchResultsLoadingGeneration &&
        (shouldFinalize?.() ?? true);

      try {
        self.loading.members.loadingStates();

        const response: { results: Array<{ members: IMember[] }> } =
          yield Areas.GetFamiliesByArea({
            account__area__id: areaId,
            other_tenants_include: other_tenants,
          });

        const members: IMember[] = response.results.flatMap(
          (item) => item.members
        );
        const uniqueMembers = uniqBy(members, 'id');

        if (canApply()) {
          self.memberId = uniqueMembers[0]?.id || '';
          self.members.replace(
            uniqueMembers.map((member) => MemberBaseModel.create(member))
          );
          self.loading.members.successStates();
        }

        return members;
      } catch (e) {
        if (canApply()) {
          self.loading.members.errorStates();
          console.error(e);
        }

        return [];
      } finally {
        if (canFinalize()) {
          self.loading.members.finallyStates();
        }
      }
    }),
    getWorkersByPhone: flow(function* (
      value: string,
      shouldApply?: ApplicantSearchGuard,
      shouldFinalize?: ApplicantSearchGuard
    ) {
      const generation = self.searchResultsGeneration;
      const loadingGeneration = self.searchResultsLoadingGeneration;
      const canApply = () =>
        generation === self.searchResultsGeneration &&
        (shouldApply?.() ?? true);
      const canFinalize = () =>
        loadingGeneration === self.searchResultsLoadingGeneration &&
        (shouldFinalize?.() ?? true);

      try {
        self.loading.workers.loadingStates();

        const phone = splitPhone(value);
        const response: { results: IWorkerNew[] } =
          yield Workers.GetWorkersCardsByPhone({
            phones__str_number: phone.code + phone.number,
            is_dismiss__ne: true,
          });
        const workers = response.results.map((worker) => worker);

        if (canApply()) {
          if (workers.length) {
            self.workerId = workers[0]?.id;
          }

          self.workers = cast(workers);
          self.loading.workers.successStates();
        }

        return workers;
      } catch (e) {
        if (canApply()) {
          self.loading.workers.errorStates();
          console.error(e);
        }

        return [];
      } finally {
        if (canFinalize()) {
          self.loading.workers.finallyStates();
        }
      }
    }),
    setTenantContacts: flow(function* (
      id: string,
      params: { [key: string]: any }
    ) {
      try {
        self.loading.addContacts.loadingStates();

        const newParams = killNulls(params);
        const response: any = yield Tenants.SetTenantContacts(id, newParams);

        let contact;

        switch (self.contactType) {
          case 'tenant':
            contact = self.members.find(
              (item: IMember): boolean => item.id === id
            );
            break;

          case 'worker':
            contact = self.workers.find(
              (item: IWorkerNew): boolean => item.id === id
            );
            break;

          default:
            break;
        }

        contact?.setPhones(response.phones);

        self.loading.addContacts.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.loading.addContacts.errorStates();
      } finally {
        self.loading.addContacts.finallyStates();
      }
    }),
    setEmployeeContacts: flow(function* (
      id: string,
      params: { [key: string]: any }
    ) {
      try {
        self.loading.addContacts.loadingStates();

        const newParams = killNulls(params);
        const response: any = yield Tenants.SetEmployeeContacts(id, newParams);

        const worker = self.workers.find(
          (item: IWorkerNew): boolean => item.id === id
        );

        worker?.setPhones(response.phones);

        self.loading.addContacts.successStates();

        return response;
      } catch (error) {
        console.error(error);
        self.loading.addContacts.errorStates();

        return Promise.reject(error);
      } finally {
        self.loading.addContacts.finallyStates();
      }
    }),
    setOtherPersonContacts: flow(function* (
      id: string,
      params: { [key: string]: any }
    ) {
      try {
        self.loading.addContacts.loadingStates();

        const response: any = yield Tenants.SetOtherPersonContacts(id, params);

        const otherPersons = getRoot<{ appeals: IAppealsStore }>(self).appeals
          .others.persons;
        const contact = otherPersons.find((item): boolean => item._id === id);
        contact?.setPhones(response.phone_numbers);
        self.loading.addContacts.successStates();

        return response;
      } catch (error) {
        console.error(error);
        self.loading.addContacts.errorStates();

        return Promise.reject(error);
      } finally {
        self.loading.addContacts.finallyStates();
      }
    }),
    setOtherEmployeeContacts: flow(function* (
      id: string,
      params: { [key: string]: any }
    ) {
      try {
        self.loading.addContacts.loadingStates();

        const response: any = yield Tenants.SetOtherEmployeeContacts(
          id,
          params
        );

        const otherEmployees = getRoot<{ appeals: IAppealsStore }>(self).appeals
          .others.employees;
        const contact = otherEmployees.find((item): boolean => item._id === id);
        contact?.setPhones(response.phone_numbers);
        self.loading.addContacts.errorStates();

        return response;
      } catch (error) {
        console.error(error);
        self.loading.addContacts.errorStates();

        return Promise.reject(error);
      } finally {
        self.loading.addContacts.finallyStates();
      }
    }),
  }));

const SelectionModel = types
  .model('SelectionModel', {
    departments: types.optional(types.array(types.string), []),
    positions: types.optional(types.array(types.string), []),
    workers: types.optional(types.array(types.string), []),
  })
  .views((self) => ({
    get snapshot() {
      return getSnapshot(self);
    },
  }))
  .actions((self) => ({
    updateField<K extends keyof typeof self.snapshot>(
      fieldName: K,
      value: string[]
    ) {
      self[fieldName].replace(value);
    },
  }));

export const SubjectModel = types
  .model('SubjectModel', {
    _id: types.optional(types.maybeNull(types.string), ''),
    name: types.optional(types.string, ''),
    deadline: types.optional(types.number, 30),
    isAnotherSubject: types.optional(types.boolean, false),
  })
  .views((self) => ({
    get snapshot() {
      return getSnapshot(self);
    },
    get deadlineDate(): moment.Moment {
      return moment()
        .startOf('day')
        .add(self.deadline + 1, 'days');
    },
    get formatDeadline(): string {
      return `Срок выполнения: до ${this.deadlineDate.format('DD.MM.YYYY')} (${
        self.deadline
      } дней)`;
    },
  }))
  .actions((self) => ({
    updateField<K extends keyof typeof self.snapshot>(
      fieldName: K,
      value: (typeof self)[K]
    ) {
      self[fieldName] = value;
    },
    reset() {
      self._id = '';
      self.name = '';
      self.deadline = 30;
    },
  }));

export const AppealCreateSettingsModel = types
  .model('AppealCreateSettings', {
    appealer: types.optional(ContactModel, {}),
    addNewThirdPartyModal: types.optional(AddNewThirdPartyModal, {}),
    selectedTab: types.optional(types.enumeration(CreateTabsList), 'appealer'),
    phone: types.optional(types.string, ''),
    previousPhone: types.optional(types.string, ''),
    lastValidPhoneNumber: types.optional(types.string, ''),
    portalProblemLink: types.optional(types.string, ''),
    observers: types.optional(SelectionModel, {}),
    executor: types.optional(SelectionModel, {}),
    subject: types.optional(SubjectModel, {}),
    contactSearchSource: types.optional(
      types.maybeNull(
        types.enumeration('ApplicantSearchSource', ['phone', 'address'])
      ),
      null
    ),
    appealerSearchStarted: types.optional(types.boolean, false), // для UI-логики (показывать/скрывать блоки)
    socialNetworks: types.optional(
      types.frozen<{ [propName: string]: any }>(),
      {}
    ),
    loading: types.optional(RequestsModel, {}),
    // ===== Помещения =====
    selectedHouseID: types.optional(types.string, ''),
    selectedAreaID: types.optional(types.string, ''),
    areas: types.optional(types.array(AreaModel), []),
    manuallySelectedArea: types.optional(AreaModel, {}),
    manuallySelectedHouse: types.optional(HouseModel, {}),
    anotherAddress: types.optional(types.boolean, false),
    anotherMember: types.optional(types.boolean, false),
    // ===== Файлы =====
    selectedFiles: types.optional(types.frozen<Array<AppealFile>>(), []),
    // Блокирует кнопку "Создать" на время переноса вложений из сообщения
    // чата (см. attachChatMessageFilesToAppeal в appeals/utils.ts) — иначе
    // обращение можно создать раньше, чем вложения успеют загрузиться.
    lockUploadAppealFiles: types.optional(types.boolean, false),

    // ===== сейчас ведут только к PortalForm - заменить по возможности =====
    isAnotherAddress: types.optional(types.boolean, false),
    applicantType: types.optional(
      types.enumeration(ApplicantTypesList),
      'tenant'
    ),
    // ===== Жилец =====
    selectedMemberID: types.optional(types.string, ''),
    // ===== Сотрудник =====
    selectedWorkerID: types.optional(types.string, ''),
    // ===== Сторонние лица =====
    selectedOtherPersonID: types.optional(types.string, ''),
    selectedOtherProviderID: types.optional(types.string, ''),
    selectedOtherEmployeeID: types.optional(types.string, ''),
    // ======================================================================
  })
  .views((self) => ({
    get snapshot() {
      return getSnapshot(self);
    },
    get portalProblemNumber(): string {
      if (!self.portalProblemLink.startsWith(PROBLEM_LINK_PREFIX)) {
        return '';
      }

      const problemNumber = self.portalProblemLink.slice(
        PROBLEM_LINK_PREFIX.length
      );

      return /^\d+$/.test(problemNumber) ? problemNumber : '';
    },
    get isPortalProblemLinkValid(): boolean {
      return Boolean(this.portalProblemNumber);
    },
    get isShowBackButton(): boolean {
      return self.selectedTab !== 'appealer';
    },
    get formatExecutor() {
      if (!self.executor.workers[0]) {
        return null;
      }

      return {
        _id: self.executor.workers[0],
      };
    },
    get formatObservers() {
      const employees = self.observers.workers.map((el) => ({ _id: el }));

      return killNulls({
        employees: !!employees.length ? employees : null,
        departments:
          !!self.observers.departments.length && !employees.length
            ? self.observers.departments.map((el) => ({ _id: el }))
            : null,
      });
    },
    get selectedArea(): IArea {
      const selectedArea =
        self.areas.find(
          (item: IArea): boolean => item.id === self.selectedAreaID
        ) || self.manuallySelectedArea;

      return cloneDeep(selectedArea);
    },
  }))
  .volatile(() => ({
    appealerSearchToken: 0, // для логики с отменой запросов
    appealerSearchResultsGeneration: 0,
    appealerSearchLoadingGeneration: 0,
  }))
  .actions((self) => ({
    updateField<K extends keyof typeof self.snapshot>(
      fieldName: K,
      value: (typeof self)[K]
    ) {
      self[fieldName] = value;
    },
    setFiles(files: Array<AppealFile>) {
      self.selectedFiles = files;
    },
    setLockUploadAppealFiles(value: boolean) {
      self.lockUploadAppealFiles = value;
    },
  }))
  .actions((self) => ({
    setBooleanProperties(
      key: 'anotherAddress' | 'anotherMember',
      bool: boolean
    ) {
      self[key] = bool;
    },
    setAddNewThirdPartyModalProperties(
      key: 'show' | 'addOrganization',
      bool: boolean
    ) {
      self.addNewThirdPartyModal[key] = bool;
    },
    setManuallySelectedArea(area: IArea) {
      self.manuallySelectedArea = cast(area);
    },
    setManuallySelectedHouse(house: IHouse) {
      self.manuallySelectedHouse = cast(house);
    },
    clearSearchResultsPreservingRequests() {
      self.appealerSearchResultsGeneration += 1;
      self.socialNetworks = {};
      self.appealer.clearSearchResultsPreservingRequests();
      applySnapshot(self.areas, []);

      Object.entries(self.loading).forEach(([key, value]) => {
        if (self.loading[key as keyof typeof self.loading].requestCount === 0) {
          value.clearStates();
        }
      });
    },
  }))
  .actions((self) => ({
    clearCreateRequest(fullReset: boolean = false) {
      if (fullReset) {
        self.appealerSearchToken += 1;
        self.appealerSearchResultsGeneration += 1;
        self.appealerSearchLoadingGeneration += 1;
        self.socialNetworks = {};
        applySnapshot(self.loading, {});
        self.appealer.invalidateSearchResultsForFullReset();
        applySnapshot(self.appealer, {});
      } else {
        self.clearSearchResultsPreservingRequests();
      }

      self.selectedTab = 'appealer';
      self.selectedAreaID = '';
      self.selectedMemberID = '';
      self.selectedWorkerID = '';
      self.selectedOtherPersonID = '';
      self.portalProblemLink = '';
      applySnapshot(self.areas, []);
      applySnapshot(self.manuallySelectedHouse, {});
      applySnapshot(self.manuallySelectedArea, {});
      self.anotherAddress = false;
      self.anotherMember = false;
      applySnapshot(self.addNewThirdPartyModal, {});

      if (fullReset) {
        self.previousPhone = '';
        self.contactSearchSource = null;
        self.appealerSearchStarted = false;
      }
    },
    getSocialNetworksByPhone: flow(function* (
      phone: string,
      shouldApply?: ApplicantSearchGuard,
      shouldFinalize?: ApplicantSearchGuard
    ) {
      if (!/^\d{10}$/.test(phone)) {
        return;
      }

      const generation = self.appealerSearchResultsGeneration;
      const loadingGeneration = self.appealerSearchLoadingGeneration;
      const canApply = () =>
        generation === self.appealerSearchResultsGeneration &&
        (shouldApply?.() ?? true);
      const canFinalize = () =>
        loadingGeneration === self.appealerSearchLoadingGeneration &&
        (shouldFinalize?.() ?? true);

      try {
        self.loading.socials.loadingStates();

        const response = yield Tenants.GetNumberSocialNetworks({
          phone_number: phone,
        });

        if (canApply()) {
          self.socialNetworks = response;
          self.loading.socials.successStates();
        }

        return response;
      } catch (e) {
        if (canApply()) {
          self.loading.socials.errorStates();
          console.error(e);
        }
      } finally {
        if (canFinalize()) {
          self.loading.socials.finallyStates();
        }
      }
    }),
    getAreasByPhone: flow(function* (
      strPhone: string,
      shouldApply?: ApplicantSearchGuard,
      shouldFinalize?: ApplicantSearchGuard
    ) {
      const generation = self.appealerSearchResultsGeneration;
      const loadingGeneration = self.appealerSearchLoadingGeneration;
      const canApply = () =>
        generation === self.appealerSearchResultsGeneration &&
        (shouldApply?.() ?? true);
      const canFinalize = () =>
        loadingGeneration === self.appealerSearchLoadingGeneration &&
        (shouldFinalize?.() ?? true);

      try {
        self.loading.areas.loadingStates();

        const phone = splitPhone(strPhone);
        const response: { results: ITenant[] } =
          yield Tenants.GetTenantsCardsByPhone({
            phones__str_number: phone.code + phone.number,
          });
        const areas = response.results.map((item) => {
          return item.area;
        });
        const uniqueAreas = uniqBy(areas, 'id');

        if (canApply()) {
          if (!!uniqueAreas.length) {
            self.selectedHouseID = uniqueAreas[0].house.id;
            self.selectedAreaID = uniqueAreas[0].id;
          }

          self.areas = cast(uniqueAreas);

          self.loading.areas.successStates();
        }

        return uniqueAreas;
      } catch (e) {
        if (canApply()) {
          self.loading.areas.errorStates();
          console.error(e);
        }

        return [];
      } finally {
        if (canFinalize()) {
          self.loading.areas.finallyStates();
        }
      }
    }),
  }))
  .actions((self) => ({
    invalidateAppealerSearch(source: 'phone' | 'address') {
      self.contactSearchSource = source;
      self.appealerSearchStarted = true;
      self.appealerSearchToken += 1;
      self.clearSearchResultsPreservingRequests();
    },
    // appealerSearchStarted иначе выставляется только внутри
    // searchAppealerByPhone/searchAppealerByAddress-поиска (см. Appealer —
    // блок "Тип заявителя" скрыт, пока appealerSearchStarted === false).
    // Нужен, когда тип заявителя проставляется программно (не через
    // реальный поиск по телефону), чтобы этот блок сразу отрисовался.
    markAppealerSearchStarted() {
      self.appealerSearchStarted = true;
    },
  }))
  .actions((self) => ({
    searchAppealerByAddress: flow(function* (
      areaId: string,
      context: ApplicantSearchContext = {}
    ) {
      self.contactSearchSource = 'address';
      self.appealerSearchStarted = true;
      self.appealerSearchToken += 1;
      self.clearSearchResultsPreservingRequests();

      const searchToken = self.appealerSearchToken;
      const loadingGeneration = self.appealerSearchLoadingGeneration;
      const isCurrent = () => searchToken === self.appealerSearchToken;
      const shouldFinalize = () =>
        loadingGeneration === self.appealerSearchLoadingGeneration;
      const members: IMember[] | undefined =
        yield self.appealer.getMembersByArea(
          areaId,
          true,
          isCurrent,
          shouldFinalize
        );

      if (!isCurrent()) {
        return members;
      }

      const selectedMember = members?.[0];

      if (selectedMember) {
        if (selectedMember.id) {
          safelyInvoke(
            context.onTenantSelected,
            selectedMember.id,
            areaId,
            isCurrent
          );
        }
      } else if (self.appealer.loading.members.error) {
        self.setBooleanProperties('anotherMember', true);
      }

      return members;
    }),
    searchAppealerByPhone: flow(function* (
      phone: string,
      context: ApplicantSearchContext = {}
    ) {
      self.contactSearchSource = 'phone';
      self.appealerSearchStarted = true;
      self.appealerSearchToken += 1;

      const searchToken = self.appealerSearchToken;
      const loadingGeneration = self.appealerSearchLoadingGeneration;
      const isCurrent = () => searchToken === self.appealerSearchToken;
      const shouldFinalize = () =>
        loadingGeneration === self.appealerSearchLoadingGeneration;

      self.clearCreateRequest();
      context.clearRelatedData?.();
      self.updateField('lastValidPhoneNumber', phone);
      safelyInvoke(context.searchSocialNetworks, phone, isCurrent);

      const areas: IArea[] | undefined = yield self.getAreasByPhone(
        phone,
        isCurrent,
        shouldFinalize
      );

      if (!isCurrent()) {
        return;
      }

      const firstArea = areas?.[0];

      self.appealer.setContactType('worker');
      const workersSearch = self.appealer.getWorkersByPhone(
        phone,
        isCurrent,
        shouldFinalize
      );

      if (firstArea?.house.id) {
        safelyInvoke(context.loadHouse, firstArea.house.id, isCurrent);
      }

      const workers: IWorkerNew[] | undefined = yield workersSearch;

      if (!isCurrent()) {
        return;
      }

      if (firstArea) {
        self.appealer.setContactType('tenant');
        const members: IMember[] | undefined =
          yield self.appealer.getMembersByArea(
            firstArea.id,
            true,
            isCurrent,
            shouldFinalize
          );

        if (!isCurrent()) {
          return;
        }

        const selectedMember = members?.[0];

        if (selectedMember?.id) {
          safelyInvoke(
            context.onTenantSelected,
            selectedMember.id,
            firstArea.id,
            isCurrent
          );
        }
      } else if (!workers?.length) {
        self.appealer.setContactType('tenant');
      }
    }),
  }))
  .actions((self) => ({
    handlePhoneInput: flow(function* (
      phone: string,
      context: ApplicantSearchContext = {}
    ): Generator<any, void, any> {
      self.previousPhone = phone;

      const isInvalidPhone = !/^\d{10}$/.test(phone);
      const isRepeatedPhoneSearch =
        phone === self.lastValidPhoneNumber &&
        self.contactSearchSource === 'phone';

      if (isRepeatedPhoneSearch) {
        return;
      }

      if (isInvalidPhone) {
        self.contactSearchSource = 'phone';
        self.appealerSearchToken += 1;
        self.lastValidPhoneNumber = '';

        return;
      }

      yield self.searchAppealerByPhone(phone, context);

      const otherPersons = getRoot<{ appeals: IAppealsStore }>(self).appeals
        .others;
      yield otherPersons.getPersons(phone);
    }),
  }));
